import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "hooks/useOkoApi/useOkoApi";

// Stable empty references so consumers don't re-render on identity changes while
// a query is disabled/loading.
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

// Consumers render `error` as a message string; React Query hands back an Error
// object, so map it to the same pre-formatted string the old hooks returned.
const toErrorMessage = (error, fallback) =>
  error ? error?.detail || error?.message || fallback : null;

export const useCurricula = () => {
  const [curricula, setCurricula] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await request("curriculum", { method: "GET" });
        if (!cancelled) setCurricula(data);
      } catch {
        if (!cancelled) setCurricula([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, []);

  return { curricula, loading };
};

// Query-key + fetcher builders are shared between each read hook and its
// prefetch helper so warming the cache targets the exact same cache entry.
export const curriculumCoursesQueryKey = (curriculumId) => ["curriculum", curriculumId, "courses"];
const fetchCurriculumCourses = (curriculumId) =>
  request(`curriculum/${curriculumId}/courses`, { method: "GET" });

export const curriculumUnitsQueryKey = (courseId) => ["curriculum", "courses", courseId, "units"];
const fetchCurriculumUnits = (courseId) =>
  request(`curriculum/courses/${courseId}/units`, { method: "GET" });

export const useCurriculumCourses = (curriculumId) => {
  const { data, isInitialLoading, error } = useQuery({
    queryKey: curriculumCoursesQueryKey(curriculumId),
    queryFn: () => fetchCurriculumCourses(curriculumId),
    enabled: !!curriculumId,
  });
  return { courses: data ?? EMPTY_ARRAY, loading: isInitialLoading, error: toErrorMessage(error, "Failed to load courses") };
};

export const useCurriculumUnits = (courseId) => {
  const { data, isInitialLoading, error } = useQuery({
    queryKey: curriculumUnitsQueryKey(courseId),
    queryFn: () => fetchCurriculumUnits(courseId),
    enabled: !!courseId,
  });
  return { units: data ?? EMPTY_ARRAY, loading: isInitialLoading, error: toErrorMessage(error, "Failed to load units") };
};

/**
 * Warm the units cache for a course before it is selected, so the render-gated
 * useCurriculumUnits hook reads a warm (or in-flight, deduped) entry instead of
 * starting the fetch a render later. No-op when courseId is falsy.
 */
export const usePrefetchCurriculumUnits = (courseId) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!courseId) return;
    queryClient
      .prefetchQuery({ queryKey: curriculumUnitsQueryKey(courseId), queryFn: () => fetchCurriculumUnits(courseId) })
      .catch(() => {
        // Silently fail - this is just a preload.
      });
  }, [queryClient, courseId]);
};

// Every unit of every course in the curriculum, flattened for a single Unit
// dropdown that spans grades. Each entry keeps its course so options can be
// labeled "Grade 3, Unit 1" and the Lesson dropdown can cascade off
// `unit.lessons` (lessons ship embedded in the units payload — there is no
// per-unit lessons endpoint).
export const useAllCurriculumUnits = (curriculumId) => {
  const { data, isInitialLoading, error } = useQuery({
    queryKey: ["curriculum", curriculumId, "all-units"],
    queryFn: async () => {
      const courses = await fetchCurriculumCourses(curriculumId);
      const unitsPerCourse = await Promise.all(
        (courses || []).map((course) => fetchCurriculumUnits(course.id)),
      );
      return (courses || []).flatMap((course, i) =>
        (unitsPerCourse[i] || []).map((unit) => ({ course, unit })),
      );
    },
    enabled: !!curriculumId,
  });
  return {
    courseUnits: data ?? EMPTY_ARRAY,
    loading: isInitialLoading,
    error: toErrorMessage(error, "Failed to load units"),
  };
};

export const useLessonDetail = (lessonId) => {
  const { data, isInitialLoading, error, refetch } = useQuery({
    queryKey: ["curriculum", "lessons", lessonId],
    queryFn: () => request(`curriculum/lessons/${lessonId}`, { method: "GET" }),
    enabled: !!lessonId,
  });
  return { lesson: data ?? null, loading: isInitialLoading, error: toErrorMessage(error, "Failed to load lesson"), refetch };
};

// The lesson-questions endpoint now returns a paginated envelope
// { total, limit, offset, questions }. Unwrap to the questions array, tolerating
// the older bare-array shape too.
export const toQuestionsArray = (data) => (Array.isArray(data) ? data : (data?.questions ?? []));

export const QUESTION_PAGE_SIZE = 10;

// Build the query string for the lesson-questions endpoint from an optional filter.
// `standards`/`learning_components` are repeatable params; `standard_code` pins a
// single sub-item standard (prereq/extension). `question_type` defaults to "both"
// (omitted, since the server treats absent as "both"). Empty selections are dropped
// so an all-empty filter produces the same URL as the unfiltered call.
export const buildLessonQuestionsQuery = ({ limit, offset, filter } = {}) => {
  const params = new URLSearchParams();
  if (typeof limit === "number") params.set("limit", String(limit));
  if (typeof offset === "number") params.set("offset", String(offset));
  const f = filter || {};
  if (f.standardCode) params.set("standard_code", f.standardCode);
  (f.standards || []).forEach((code) => code && params.append("standards", code));
  (f.learningComponents || []).forEach((lc) => lc && params.append("learning_component", lc));
  if (f.questionType && f.questionType !== "both") params.set("question_type", f.questionType);
  return params.toString();
};

// Paginated: loads the first page eagerly and exposes loadMore() so the carousel
// can fetch the next page as the educator nears the end. `total` is the full count
// from the envelope (e.g. 141), not just the loaded slice. Backed by an infinite
// query keyed on the lesson + filter, where each page's offset is the cumulative
// count of questions loaded so far.
// `filter` (optional) — { standardCode?, standards?, learningComponents?, questionType? }
// narrows the bank; it's part of the query key, so changing it re-fetches from page 0
// and the carousel + count track the active filter. Absent/empty filter = unfiltered.
export const useLessonQuestions = (lessonId, filter) => {
  const filterKey = JSON.stringify(filter || null);
  const { data, isInitialLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["curriculum", "lessons", lessonId, "questions", filterKey],
    queryFn: ({ pageParam = 0 }) =>
      request(
        `curriculum/lessons/${lessonId}/questions?${buildLessonQuestionsQuery({ limit: QUESTION_PAGE_SIZE, offset: pageParam, filter })}`,
        { method: "GET" },
      ),
    enabled: !!lessonId,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + toQuestionsArray(page).length, 0);
      const total = typeof lastPage?.total === "number" ? lastPage.total : loaded;
      return loaded < total ? loaded : undefined;
    },
  });

  const questions = data ? data.pages.flatMap(toQuestionsArray) : EMPTY_ARRAY;
  const firstPage = data?.pages?.[0];
  const total = typeof firstPage?.total === "number" ? firstPage.total : questions.length;

  // Returns the newly fetched page (empty on no-op/failure) so the carousel can
  // advance onto it — the same on-next-at-boundary contract as the standard flow.
  const loadMore = useCallback(async () => {
    if (!hasNextPage || isFetchingNextPage) return [];
    try {
      const res = await fetchNextPage();
      const pages = res.data?.pages ?? [];
      return toQuestionsArray(pages[pages.length - 1]);
    } catch {
      return [];
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return { questions, total, loading: isInitialLoading, loadMore };
};

// Which of core / prerequisites / extensions have NO assignable questions, so the
// Assign Groups modal can disable them. The signal must be flow-scoped — the global
// standards tree's challenge_count counts the whole question bank and disagrees with
// lesson-scoped availability (e.g. a cluster core like 3.MD.B shows "no questions" in
// the lesson yet has a non-zero tree count). One request answers every code:
// GET curriculum/question-availability, with lesson_id for lesson flow. A track with
// codes but none question-bearing is returned; a track with no codes is omitted (the
// caller's hasX gate hides it). Returns [] until the fetch resolves (nothing disabled
// while loading), so start it on selection — not on modal open.
// Every requested code carries an explicit boolean, whatever the response or a failure
// gives back. EditGroupFocus tells "probed, empty" from "not probed" with a strict
// `=== false`, so a merely-absent key reads as "not probed" and leaves a question-less
// standard selectable — the guard failing open. Don't trust the response to be total.
const withEveryCode = (codes, availability) =>
  Object.fromEntries(codes.map((c) => [c, !!availability?.[c]]));

const fetchQuestionAvailability = async ({ flow, lessonId, codes }) => {
  const params = new URLSearchParams();
  codes.forEach((c) => params.append("standard_code", c));
  if (flow === "lesson") {
    // Nothing to gate on until a lesson is picked.
    if (!lessonId) return withEveryCode(codes, null);
    params.set("lesson_id", lessonId);
  } else {
    // The per-code consensus probe this replaces defaulted hasExplanations to true, so
    // "has questions" in standard flow has always meant "has an active question with an
    // active explanation". The endpoint defaults that filter off — drop this and tracks
    // that are disabled today become assignable.
    params.set("has_explanations", "true");
  }
  try {
    const data = await request(`curriculum/question-availability?${params}`, { method: "GET" });
    return withEveryCode(codes, data?.availability);
  } catch {
    // Match the old per-code behaviour on failure: resolve to all-false rather than
    // leaving `resolved` stuck false, which would silently disable the empty-track guard.
    return withEveryCode(codes, null);
  }
};

// Per-code question availability for the current flow: { [code]: boolean }, plus
// `resolved` so callers can tell "probed, none found" from "not probed yet" — the
// map is empty in both. Codes are deduped and sorted so components probing the same
// set share one React Query entry instead of each firing its own request.
export const useQuestionAvailability = ({ flow, lessonId, codes = [] }) => {
  const codesKey = JSON.stringify(codes);
  const sorted = useMemo(
    () => [...new Set(codes)].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [codesKey],
  );
  const { data } = useQuery({
    queryKey: ["question-availability", flow, lessonId ?? null, sorted],
    queryFn: () => fetchQuestionAvailability({ flow, lessonId, codes: sorted }),
    enabled: sorted.length > 0,
  });

  return {
    availability: data ?? EMPTY_OBJECT,
    resolved: sorted.length === 0 || data !== undefined,
  };
};

export const useNoQuestionTracks = ({ flow, lessonId, coreCodes = [], prereqCodes = [], extCodes = [] }) => {
  const key = JSON.stringify({ coreCodes, prereqCodes, extCodes });
  const codes = useMemo(
    () => [...new Set([...coreCodes, ...prereqCodes, ...extCodes])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  const { availability, resolved } = useQuestionAvailability({ flow, lessonId, codes });

  return useMemo(() => {
    if (!resolved) return [];
    return [
      ["core", coreCodes],
      ["prerequisites", prereqCodes],
      ["extensions", extCodes],
    ]
      // no codes → hasX already hides this track
      .map(([trackKey, group]) => (group.length && !group.some((c) => availability[c]) ? trackKey : null))
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, resolved, key]);
};

// Standard-flow counterpart of useLessonQuestions
// — GET /curriculum/standards/{standardCode}/questions. Same paginated envelope, query
// shape, and loadMore contract; only the URL's scope id changes (a standard code instead
// of a lesson id). `filter` — { standards?, learningComponents?, questionType? } (no
// `standardCode` sub-narrowing here — the standard is already the URL's scope).
export const useStandardScopedQuestions = (standardCode, filter) => {
  const filterKey = JSON.stringify(filter || null);
  const { data, isInitialLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["curriculum", "standards", standardCode, "questions", filterKey],
    queryFn: ({ pageParam = 0 }) =>
      request(
        `curriculum/standards/${standardCode}/questions?${buildLessonQuestionsQuery({ limit: QUESTION_PAGE_SIZE, offset: pageParam, filter })}`,
        { method: "GET" },
      ),
    enabled: !!standardCode,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + toQuestionsArray(page).length, 0);
      const total = typeof lastPage?.total === "number" ? lastPage.total : loaded;
      return loaded < total ? loaded : undefined;
    },
  });

  const questions = data ? data.pages.flatMap(toQuestionsArray) : EMPTY_ARRAY;
  const firstPage = data?.pages?.[0];
  const total = typeof firstPage?.total === "number" ? firstPage.total : questions.length;

  const loadMore = useCallback(async () => {
    if (!hasNextPage || isFetchingNextPage) return [];
    try {
      const res = await fetchNextPage();
      const pages = res.data?.pages ?? [];
      return toQuestionsArray(pages[pages.length - 1]);
    } catch {
      return [];
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return { questions, total, loading: isInitialLoading, loadMore };
};

export const useStandardQuestions = (lessonId, standardCode) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lessonId || !standardCode) {
      setQuestions([]);
      return;
    }
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ standard_code: standardCode });
        const data = await request(
          `curriculum/lessons/${lessonId}/questions?${params.toString()}`,
          { method: "GET" },
        );
        if (!cancelled) setQuestions(toQuestionsArray(data));
      } catch {
        if (!cancelled) setQuestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [lessonId, standardCode]);

  return { questions, loading };
};

// The group-assignments endpoint returns a classroom's *bundles* as
// { lessons: BundleOut[], unassigned_codes: [] } — the `lessons` field is named
// for history but holds BOTH kinds. Each bundle carries a `bundle_type`
// ("lesson" | "standard") discriminator: lesson bundles have a non-null `lesson`,
// standard (differentiation) bundles have a non-null `standard`. Normalize every
// variant (unified list, legacy single { lesson, assignments }) into one shape so
// consumers always read `lessons` / `unassigned_codes` with a stable per-bundle
// shape and can switch on `bundle_type`.
const EMPTY_GROUP_ASSIGNMENTS = { lessons: [], unassigned_codes: [] };

const normalizeBundle = (b) => ({
  assignment_id: b?.assignment_id ?? null,
  // Older responses predate bundle_type — infer it from which detail is present,
  // defaulting to "lesson" (the only kind those responses carried).
  bundle_type: b?.bundle_type || (b?.standard ? "standard" : "lesson"),
  lesson: b?.lesson || null,
  standard: b?.standard || null,
  last_updated_at: b?.last_updated_at ?? null,
  assignments: b?.assignments || [],
});

export const normalizeGroupAssignments = (data) => {
  if (Array.isArray(data?.lessons)) {
    return { lessons: data.lessons.map(normalizeBundle), unassigned_codes: data.unassigned_codes || [] };
  }
  if (data?.lesson) {
    return {
      lessons: [normalizeBundle({ assignment_id: data.assignment_id, lesson: data.lesson, assignments: data.assignments })],
      unassigned_codes: data.unassigned_codes || [],
    };
  }
  return { lessons: [], unassigned_codes: data?.unassigned_codes || [] };
};

// Cached so a return navigation renders the previous envelope instantly, but
// stale immediately: lessons are assigned/edited/archived on other pages that
// don't invalidate this key, so every mount revalidates in the background.
// `loading` is true only while the FIRST load for a class is in flight — the
// window the Groups empty-state gate cares about (OKO2-3684).
export const useClassroomLessonAssignment = (classroomId) => {
  const { data, dataUpdatedAt, isInitialLoading, error, refetch } = useQuery({
    queryKey: ["group-assignments", classroomId],
    queryFn: () =>
      request(`curriculum/classrooms/${classroomId}/group-assignments`, { method: "GET" })
        .then(normalizeGroupAssignments)
        // A classroom with nothing assigned is an ordinary state, not a failure.
        .catch((e) => {
          if (e?.status === 404) return EMPTY_GROUP_ASSIGNMENTS;
          throw e;
        }),
    enabled: Boolean(classroomId),
    staleTime: 0,
  });

  return {
    assignment: data || EMPTY_GROUP_ASSIGNMENTS,
    loading: isInitialLoading,
    error: toErrorMessage(error, "Failed to load assignment"),
    refetch,
    // When the envelope was last read.
    updatedAt: dataUpdatedAt,
  };
};

// `questionFilters` (optional) — a TrackQuestionFilter[] carrying the teacher's
// Advanced Selection into the assignment. Omit entirely (undefined/empty) to keep the
// frictionless unfiltered path; the backend matches each created group to its filter by
// (track_type, standard_code). A 400 is thrown if a track has zero questions at all.
export const assignLesson = async (classroomId, lessonId, distribution, questionFilters) => {
  return request(`curriculum/classrooms/${classroomId}/assign-lesson`, {
    method: "POST",
    payload: {
      lesson_id: lessonId,
      ...(distribution ? { distribution } : {}),
      ...(questionFilters?.length ? { question_filters: questionFilters } : {}),
    },
  });
};

// Swap the lesson on a single lesson bundle. Assignment-scoped: a classroom-level
// change-lesson would be ambiguous with multiple lesson bundles. `assignment_id`
// comes from the unified group-assignments GET.
// `questionFilters` (optional) — TrackQuestionFilter[] carrying the teacher's Advanced
// Selection into the changed assignment (same contract as assign-lesson). Omit when
// empty to keep the frictionless unfiltered path.
export const changeLesson = async (classroomId, assignmentId, lessonId, distribution, groupCodes, questionFilters) => {
  return request(`curriculum/classrooms/${classroomId}/assignments/${assignmentId}/change-lesson`, {
    method: "PUT",
    // `group_codes` redefines the bundle's codes; omit when empty (the backend
    // rejects an empty array) so it keeps the current codes unchanged.
    payload: {
      lesson_id: lessonId,
      ...(distribution ? { distribution } : {}),
      ...(groupCodes?.length ? { group_codes: groupCodes } : {}),
      ...(questionFilters?.length ? { question_filters: questionFilters } : {}),
    },
  });
};

// Bulk-update the group assignments for a single lesson assignment (CLA). The
// backend keys this on the assignment_id — the classroom-level collection route
// is GET-only — so the id is required.
// PUT = REPLACE: sets the bundle to exactly the sent codes (omitted codes dropped).
// Retained for the whole-set caller (EditGroupAssignmentsModal). Prefer the PATCH
// delta below for lesson-flow edits.
export const updateGroupAssignments = async (classroomId, assignmentId, assignments) => {
  return request(`curriculum/classrooms/${classroomId}/assignments/${assignmentId}/group-assignments`, {
    method: "PUT",
    payload: { assignments },
  });
};

// PATCH = DELTA (lesson-flow): touches only the codes sent — track_type:null
// releases a code to the pool, unlisted codes are left untouched, and a code owned
// by another bundle is auto-released (interchange) instead of 400'd.
export const patchGroupAssignments = async (classroomId, assignmentId, assignments) => {
  return request(`curriculum/classrooms/${classroomId}/assignments/${assignmentId}/group-assignments`, {
    method: "PATCH",
    payload: { assignments },
  });
};

// ---- Question sets (immutable, shared snapshots a group code points at) ----
// Every save creates a NEW set and repoints the group; the server also syncs
// filter_state onto the group's question_filter and retires orphaned sets.
export const createQuestionSet = async (classroomId, payload) => {
  return request(`curriculum/classrooms/${classroomId}/question-sets`, {
    method: "POST",
    payload,
  });
};

// The learning components available on a standard's questions, as { lc_id,
// description }. Quick groups store only the ids their filter selected, so the card
// resolves display names through this rather than carrying them on the group row
// (bundle cards get them resolved server-side on the assignment).
export const getStandardLearningComponents = async (standardCode) => {
  return request(`curriculum/standards/${encodeURIComponent(standardCode)}/learning-components`, {
    method: "GET",
  });
};

export const getQuestionSet = async (questionSetId) => {
  return request(`curriculum/question-sets/${questionSetId}`, { method: "GET" });
};

// One page of a scoped question query. The server orders each page by
// (updated_at, id), so offset paging never repeats or skips a question.
export const fetchScopedQuestionsPage = async ({
  lessonId,
  standardCode,
  filter,
  limit = 20,
  offset = 0,
} = {}) => {
  const qs = buildLessonQuestionsQuery({ limit, offset, filter });
  const route = lessonId
    ? `curriculum/lessons/${lessonId}/questions?${qs}`
    : `curriculum/standards/${standardCode}/questions?${qs}`;
  const data = await request(route, { method: "GET" });
  const questions = toQuestionsArray(data);
  return { questions, total: typeof data?.total === "number" ? data.total : questions.length };
};

// Every page at once, for callers that need the whole bank (Add All, and the
// saved-set preview joining question ids against it). 50 is the server's max
// page size — asking for more is silently clamped.
export const fetchAllScopedQuestions = async ({ lessonId, standardCode, filter } = {}) => {
  const all = [];
  let offset = 0;
  let total = Infinity;
  while (offset < total) {
    // eslint-disable-next-line no-await-in-loop
    const { questions: page, total: pageTotal } = await fetchScopedQuestionsPage({
      lessonId, standardCode, filter, limit: 50, offset,
    });
    all.push(...page);
    total = pageTotal;
    if (page.length === 0) break;
    offset = all.length;
  }
  return { questions: all, total: all.length };
};

// Points other (same-classroom, assigned) group codes at an existing set; also
// copies the set's filter_state onto each group's question_filter.
export const applyQuestionSet = async (questionSetId, groupCodes) => {
  return request(`curriculum/question-sets/${questionSetId}/apply`, {
    method: "POST",
    payload: { group_codes: groupCodes },
  });
};

// Archive = hard-delete one lesson assignment (drops the CLA + its group rows and
// clears each freed code's prefix server-side). Scoped to the assignment_id; the
// collection route does not allow DELETE.
export const archiveLesson = async (classroomId, assignmentId) => {
  return request(`curriculum/classrooms/${classroomId}/assignments/${assignmentId}`, {
    method: "DELETE",
  });
};
